/**
 * /wachtlijst — wat Jasper (werkbank) en kantoor neerleggen om op te lossen.
 * Leest en schrijft /api/wachtlijst (zelfde tabel als scripts/werkbank/wacht.py
 * en de ochtendbriefing). Afvinken met een oplossing in gewone taal: de
 * briefing meldt die de volgende ochtend terug aan wie het punt neerlegde.
 */
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../components/ui/Card';
import { supabase } from '../lib/supabase';

type Status = 'open' | 'opgelost' | 'vervallen';

interface Punt {
  id: number;
  tijd: string;
  topic: string | null;
  actor: string;
  vraag: string;
  status: Status;
  opgelost_op: string | null;
  ref: string | null;
  oplossing: string | null;
  teruggemeld_op: string | null;
}

async function officeFetch(path: string, method = 'GET', body?: unknown): Promise<unknown> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `API error: ${response.status}`);
  }
  return response.json();
}

const datumNl = (iso: string) =>
  new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });

const dagenGeleden = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

const STATUS_LABEL: Record<Status, string> = { open: 'Open', opgelost: 'Opgelost', vervallen: 'Vervallen' };
const STATUS_KLEUR: Record<Status, string> = {
  open: 'bg-amber-50 text-amber-800 ring-amber-200',
  opgelost: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  vervallen: 'bg-gray-100 text-gray-600 ring-gray-200',
};

function Sluiten({ punt, soort, onKlaar, onAnnuleer }: {
  punt: Punt;
  soort: 'opgelost' | 'vervallen';
  onKlaar: () => void;
  onAnnuleer: () => void;
}) {
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const opgelost = soort === 'opgelost';

  const verstuur = async () => {
    setBezig(true);
    setFout(null);
    try {
      await officeFetch(`/api/wachtlijst?action=${soort}`, 'POST', { id: punt.id, oplossing: tekst });
      onKlaar();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Onbekende fout');
      setBezig(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <label htmlFor={`sluit-${punt.id}`} className="block text-sm font-medium text-gray-800">
        {opgelost ? 'Wat is er gedaan?' : 'Waarom is dit niet meer nodig?'}
      </label>
      <p className="mt-0.5 text-xs text-gray-500">
        {opgelost
          ? `In gewone taal; ${punt.actor} leest dit morgenochtend in de briefing.`
          : 'Korte reden; het punt verdwijnt uit de open lijst.'}
      </p>
      <textarea
        id={`sluit-${punt.id}`}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        rows={3}
        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        placeholder={opgelost ? 'Bijv. "Route-afronden telt nu alle kleden; live sinds vandaag."' : 'Bijv. "Dubbel met #41."'}
      />
      {fout && <p className="mt-2 text-sm text-red-600">{fout}</p>}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => void verstuur()}
          disabled={bezig || tekst.trim().length < 3}
          className={clsx(
            'rounded-md px-3 py-1.5 text-sm font-medium text-white',
            opgelost ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-600 hover:bg-gray-700',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {bezig ? 'Bezig…' : opgelost ? 'Afvinken' : 'Laten vervallen'}
        </button>
        <button type="button" onClick={onAnnuleer} className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">
          Annuleren
        </button>
      </div>
    </div>
  );
}

function NieuwPunt({ onKlaar }: { onKlaar: (bestond: boolean) => void }) {
  const [vraag, setVraag] = useState('');
  const [ref, setRef] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const verstuur = async () => {
    setBezig(true);
    setFout(null);
    try {
      const r = (await officeFetch('/api/wachtlijst?action=nieuw', 'POST', { vraag, ref: ref || undefined })) as { bestond: boolean };
      setVraag('');
      setRef('');
      onKlaar(r.bestond);
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Onbekende fout');
    } finally {
      setBezig(false);
    }
  };

  return (
    <Card className="p-4">
      <h2 className="text-sm font-semibold text-gray-900">Nieuw punt</h2>
      <p className="mt-0.5 text-xs text-gray-500">
        Iets dat mis is of beter kan, of wat een klant zei. Zet er een ordernummer bij als dat er is.
      </p>
      <textarea
        id="wacht-nieuw-vraag"
        value={vraag}
        onChange={(e) => setVraag(e.target.value)}
        rows={2}
        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        placeholder="Wat er mis is of beter kan"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          id="wacht-nieuw-ref"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          className="w-40 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="WB-324469 (optioneel)"
        />
        <button
          type="button"
          onClick={() => void verstuur()}
          disabled={bezig || vraag.trim().length < 8}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {bezig ? 'Bezig…' : 'Op de lijst'}
        </button>
        {fout && <span className="text-sm text-red-600">{fout}</span>}
      </div>
    </Card>
  );
}

export function WachtlijstPage() {
  const [punten, setPunten] = useState<Punt[]>([]);
  const [alles, setAlles] = useState(false);
  const [melder, setMelder] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [sluit, setSluit] = useState<{ id: number; soort: 'opgelost' | 'vervallen' } | null>(null);

  const laad = async (metAlles = alles) => {
    setLoading(true);
    setError(null);
    try {
      const r = (await officeFetch(`/api/wachtlijst${metAlles ? '?alles=1' : ''}`)) as { punten: Punt[] };
      setPunten(r.punten);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Onbekende fout');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void laad(alles); }, [alles]); // eslint-disable-line react-hooks/exhaustive-deps

  const melders = useMemo(() => {
    const t = new Map<string, number>();
    for (const p of punten) if (p.status === 'open') t.set(p.actor, (t.get(p.actor) ?? 0) + 1);
    return [...t.entries()].sort((a, b) => b[1] - a[1]);
  }, [punten]);

  const zichtbaar = useMemo(
    () => punten.filter((p) => !melder || p.actor === melder).slice().sort((a, b) => (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1) || a.tijd.localeCompare(b.tijd)),
    [punten, melder],
  );
  const openAantal = punten.filter((p) => p.status === 'open').length;
  const oudste = punten.find((p) => p.status === 'open');

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-4xl space-y-4 p-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Wachtlijst</h1>
          <p className="mt-1 text-sm text-gray-500">
            Wat Jasper en kantoor neerleggen om op te lossen. Afvinken met een korte uitleg; de melder hoort het de volgende ochtend in de briefing.
          </p>
        </div>

        <NieuwPunt
          onKlaar={(bestond) => {
            setMelding(bestond ? 'Dit punt stond al op de lijst.' : 'Op de lijst gezet.');
            void laad();
          }}
        />

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-gray-800">
            {openAantal} open{oudste ? `, oudste ${dagenGeleden(oudste.tijd)} dagen` : ''}
          </span>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={() => setMelder(null)}
            className={clsx('rounded-full px-2.5 py-0.5', !melder ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')}
          >
            Iedereen
          </button>
          {melders.map(([naam, n]) => (
            <button
              key={naam}
              type="button"
              onClick={() => setMelder(naam === melder ? null : naam)}
              className={clsx('rounded-full px-2.5 py-0.5', melder === naam ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')}
            >
              {naam} <span className="opacity-70">{n}</span>
            </button>
          ))}
          <span className="ml-auto">
            <label className="flex items-center gap-1.5 text-gray-600">
              <input id="wacht-alles" type="checkbox" checked={alles} onChange={(e) => setAlles(e.target.checked)} />
              Ook opgelost en vervallen
            </label>
          </span>
        </div>

        {melding && (
          <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
            {melding} <button type="button" className="ml-2 underline" onClick={() => setMelding(null)}>sluiten</button>
          </div>
        )}
        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {loading && punten.length === 0 && <p className="text-sm text-gray-500">Laden…</p>}
        {!loading && zichtbaar.length === 0 && !error && (
          <p className="text-sm text-gray-500">Niets open. Mooi zo.</p>
        )}

        <div className="space-y-2">
          {zichtbaar.map((p) => (
            <Card key={p.id} className={clsx('p-4', p.status !== 'open' && 'opacity-75')}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-gray-500">
                <span className="font-mono">#{p.id}</span>
                <span>{datumNl(p.tijd)}</span>
                <span className="font-medium text-gray-700">{p.actor}</span>
                {p.ref && <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-700">{p.ref}</span>}
                <span className={clsx('rounded-full px-2 py-0.5 ring-1', STATUS_KLEUR[p.status])}>{STATUS_LABEL[p.status]}</span>
                {p.status === 'open' && dagenGeleden(p.tijd) >= 14 && (
                  <span className="text-amber-700">{dagenGeleden(p.tijd)} dagen</span>
                )}
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-gray-900">{p.vraag}</p>
              {p.oplossing && (
                <p className="mt-2 border-l-2 border-emerald-300 pl-3 text-sm text-gray-700">
                  {p.oplossing}
                  <span className="ml-2 text-xs text-gray-400">
                    {p.teruggemeld_op ? `teruggemeld ${datumNl(p.teruggemeld_op)}` : 'wordt morgen teruggemeld'}
                  </span>
                </p>
              )}
              {p.status === 'open' && sluit?.id !== p.id && (
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setSluit({ id: p.id, soort: 'opgelost' })} className="rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50">
                    Afvinken
                  </button>
                  <button type="button" onClick={() => setSluit({ id: p.id, soort: 'vervallen' })} className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50">
                    Vervallen
                  </button>
                </div>
              )}
              {sluit?.id === p.id && (
                <Sluiten
                  punt={p}
                  soort={sluit.soort}
                  onAnnuleer={() => setSluit(null)}
                  onKlaar={() => {
                    setSluit(null);
                    setMelding(sluit.soort === 'opgelost' ? `#${p.id} afgevinkt; ${p.actor} hoort het morgen.` : `#${p.id} vervallen.`);
                    void laad();
                  }}
                />
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WachtlijstPage;
